import i18n from "../language/i18n";
import { isAiUsageLimitError } from "./AiRefusalUtilization";

/**
 * Turns a failed request into an i18n key that is safe to show a user.
 *
 * Every handler in the backend's `BaseRestControllerAdvice` puts the error key **directly** in the response
 * body as a bare string — `"sebitTokenIsNotRecognized"`, `"profileHasNoActiveSeason"`, `"notFoundErrorInfo"`
 * — not wrapped in an object. Reading `data.errorKey` therefore always yields `undefined` and silently
 * collapses every distinct backend outcome into the caller's generic fallback.
 *
 * The keys are worth surfacing: "your link expired, click the tile again", "you are not set up in CatchUpper
 * yet" and "the provider is unreachable" are three different instructions, and showing one message for all
 * three is what makes a support ticket unresolvable.
 *
 * **But an untranslated key must never reach the screen.** `i18n.t` returns the key itself when it is
 * missing, so a backend outcome nobody has translated would render as raw camelCase in the middle of a
 * Turkish sentence. `exists` is checked so an unknown key degrades to the caller's fallback instead.
 */
export const resolveBackendMessageKey = (
  err: unknown,
  fallbackMessageKey: string
): string => {
  const body = (err as any)?.response?.data;
  // A translated key still wins, so this helper's documented precedence is unchanged — the body the
  // backend chose is more specific than any status could be. It is checked FIRST deliberately: putting
  // the AI arm above it inverted that precedence for every caller, and a future 423 carrying a real
  // translated key would have been relabelled as a quota message.
  if (typeof body === "string" && body.length > 0 && i18n.exists(body)) {
    return body;
  }
  // The AI credit limit is the one backend outcome this lookup cannot resolve on its own: a 423's body
  // is the FEATURE's key (~25 of them, and the set grows with each new AI feature), and none of them is
  // translated — which is exactly why the check above falls through for them. So the status decides,
  // and every caller of this helper gains the limit message at once.
  if (isAiUsageLimitError(err)) return "AI_USAGE_LIMIT_REACHED";
  return fallbackMessageKey;
};

/**
 * The HTTP status of a failed request, or `null` when there is no response at all.
 *
 * 🚨 An axios rejection has NO `response` when the request never reached a server — a refused
 * connection, DNS failure, CORS-blocked preflight, or a timeout. Ten call sites did
 * `const { response } = err; const { status } = response;`, which THROWS on exactly those failures,
 * so the error HANDLER became the error: the real cause was replaced by a TypeError, the status
 * branch never ran, and the loading flag it was supposed to clear stayed true — leaving a spinner
 * that never resolves. Read the status through this instead, and treat `null` as "never arrived".
 */
export const resolveErrorStatus = (err: unknown): number | null => {
  const status = (err as any)?.response?.status;
  return typeof status === "number" ? status : null;
};

/**
 * True when the request never got an answer: no response, and a transport-level error code.
 *
 * Distinct from a 4xx/5xx, and worth distinguishing to the user: "the server said no" and "we could
 * not reach the server" call for different reactions. Mirrors admin-web's helper of the same name.
 */
export const isConnectionError = (err: unknown): boolean => {
  const e = err as any;
  if (!e || e.response) return false;
  return (
    e.code === "ERR_NETWORK" ||
    e.code === "ECONNABORTED" ||
    e.code === "ETIMEDOUT" ||
    e.message === "Network Error"
  );
};

/**
 * True when the platform itself is down rather than refusing this request: no answer at all, or the
 * gateway answering for a backend that is not there (502 / 503 / 504).
 *
 * A prod deploy refuses connections for a few minutes, and a stopped backend behind a live nginx answers
 * 502. Both used to reach the SSO hand-off as "invalid username or password" — sending a pupil who has no
 * password looking for one. This is the split that lets a caller say "try again in a moment" instead.
 */
export const isServerUnavailableError = (err: unknown): boolean => {
  if (isConnectionError(err)) return true;
  const status = resolveErrorStatus(err);
  return status === 502 || status === 503 || status === 504;
};
